import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import ContactAutocompleteWithAddNew from "../../components/ContactAutocompleteWithAddNew";

const initialPassenger = {
  id: null,
  used_title: "",
  used_contact: null,
  used_name: "",
  used_mobile: "",
  used_email: "",
  used_department: "",
  report_address: "",
  drop_address: "",
  used_by_id: null,
  used_by_title: "",
};

const TITLE_OPTIONS = [
  { value: "", label: "Select" },
  "Mr.",
  "Ms.",
  "Mrs.",
  "Dr.",
];

/* ------------------------------------------------------------------ */
/* Design tokens (same as the booking form)                            */
/* ------------------------------------------------------------------ */
const FIELD_H = 40; // one height for every input, select and dropdown

const C = {
  primary: "#1d4ed8",
  primaryHover: "#1e40af",
  primarySoft: "#eff6ff",
  surface: "#f8fafc",
  border: "#cbd5e1",
  borderStrong: "#94a3b8",
  text: "#1e293b",
  muted: "#475569",
};

const controlSx = {
  minWidth: 0,
  width: "100%",

  "& .MuiFormControl-root, & .MuiAutocomplete-root": {
    width: "100%",
  },

  "& .MuiInputBase-root:not(.MuiInputBase-multiline), & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root":
    {
      height: FIELD_H,
      boxSizing: "border-box",
      fontSize: 14,
      fontWeight: 500,
      color: C.text,
      backgroundColor: "#fff",
      borderRadius: "8px",
      paddingTop: 0,
      paddingBottom: 0,
    },

  "& .MuiInputBase-input:not(.MuiInputBase-inputMultiline)": {
    height: "100%",
    boxSizing: "border-box",
    padding: "0 12px",
    fontSize: 14,
    fontWeight: 500,
    color: C.text,
    WebkitTextFillColor: C.text,
  },

  "& .MuiSelect-select.MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    boxSizing: "border-box",
    padding: "0 32px 0 12px",
    minHeight: 0,
    fontSize: 14,
    fontWeight: 500,
    color: C.text,
  },

  "& .MuiAutocomplete-inputRoot": {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "0 !important",
    flexWrap: "nowrap",
  },

  "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
    height: "100%",
    padding: "0 12px !important",
    fontSize: "14px !important",
    fontWeight: 500,
    color: `${C.text} !important`,
    WebkitTextFillColor: `${C.text} !important`,
  },

  "& .MuiAutocomplete-endAdornment": { right: 6 },

  "& .MuiInputBase-multiline": {
    fontSize: 14,
    fontWeight: 500,
    color: C.text,
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "10px 12px",
  },

  "& .MuiInputLabel-root": {
    fontSize: 14,
    fontWeight: 500,
    color: C.muted,
    transform: "translate(13px, 11px) scale(1)",
  },

  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    transform: "translate(13px, -8px) scale(0.85)",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: C.primary,
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: C.border,
  },

  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: C.borderStrong,
  },

  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: C.primary,
    borderWidth: 1,
  },

  "& .MuiOutlinedInput-root.Mui-focused": {
    boxShadow: "0 0 0 3px rgba(29,78,216,0.12)",
  },

  "& .MuiFormHelperText-root": {
    fontSize: 12,
    color: C.muted,
    mt: 0.25,
    ml: 0.5,
  },

  "& .MuiSvgIcon-root": { fontSize: 18 },
};

const readOnlySx = {
  "& .MuiInputBase-root": { backgroundColor: `${C.surface} !important` },
  "& .MuiInputBase-input": {
    color: `${C.text} !important`,
    WebkitTextFillColor: `${C.text} !important`,
  },
};

const btnBase = {
  textTransform: "none",
  fontSize: 14,
  fontWeight: 600,
  borderRadius: "8px",
  boxShadow: "none",
  minWidth: 0,
  px: 2.25,
  height: 36,
};

const primaryBtn = {
  ...btnBase,
  backgroundColor: C.primary,
  "&:hover": { backgroundColor: C.primaryHover, boxShadow: "none" },
};

const ghostBtn = {
  ...btnBase,
  borderColor: C.borderStrong,
  color: C.text,
  backgroundColor: "#fff",
  "&:hover": { backgroundColor: C.surface, borderColor: C.muted },
};

const thSx = {
  fontSize: 13,
  fontWeight: 700,
  color: "#334155",
  whiteSpace: "nowrap",
  backgroundColor: C.surface,
  borderBottom: `1px solid ${C.border}`,
  px: 1.5,
  py: "2px",
  lineHeight: 1.3,
};

const tdSx = {
  fontSize: 14,
  fontWeight: 500,
  color: C.text,
  borderBottom: `1px solid ${C.border}`,
  px: 1.5,
  py: 1,
};

const wrapTd = {
  ...tdSx,
  whiteSpace: "normal",
  wordBreak: "break-word",
  maxWidth: 260,
};

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */
const Card = ({ title, action, children }) => (
  <Box
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "12px",
      backgroundColor: "#fff",
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2.5,
        py: 1.25,
        minHeight: 56,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: C.text }}>
        {title}
      </Typography>
      {action}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

const Grid = ({ cols = 4, children, sx }) => (
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: `repeat(${cols}, 1fr)`,
      },
      alignItems: "start",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Column span helper for the responsive grid
const span = (n) => ({ gridColumn: { sm: "span 2", md: `span ${n}` } });

// Wrapper for external dropdown / autocomplete components
const Slot = ({ children, sx }) => (
  <Box sx={{ ...controlSx, ...sx }}>{children}</Box>
);

// Text input / select with consistent styling
const Field = ({ options, sx, InputProps, ...props }) => {
  const locked = !!InputProps?.readOnly || !!props.disabled;
  return (
    <TextField
      size="small"
      fullWidth
      select={!!options}
      InputProps={InputProps}
      sx={{ ...controlSx, ...(locked ? readOnlySx : {}), ...sx }}
      {...props}
    >
      {options &&
        options.map((o) => {
          const opt = typeof o === "string" ? { value: o, label: o } : o;
          return (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: C.text,
              }}
            >
              {opt.label}
            </MenuItem>
          );
        })}
    </TextField>
  );
};

const IconBtn = ({ children, ...props }) => (
  <IconButton
    size="small"
    sx={{
      p: 0.75,
      borderRadius: "6px",
      "& svg": { fontSize: 17 },
      "&:hover": { backgroundColor: C.surface },
    }}
    {...props}
  >
    {children}
  </IconButton>
);

const EMPTY_LIST = [];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function PassengerSubform({
  passengers = EMPTY_LIST,
  onAdd = () => {},
  onEdit = () => {},
  onDelete = () => {},
  clientId,
  customerId,
  dropdownData = {},
}) {
  const [draft, setDraft] = useState(initialPassenger);
  const [editIndex, setEditIndex] = useState(-1);

  // Always holds the latest passengers without making the effect below
  // depend on it (a new array from the parent must NOT reset the draft).
  const passengersRef = useRef(passengers);
  passengersRef.current = passengers;

  // Load the draft only when the user starts / stops editing a row.
  useEffect(() => {
    const row = passengersRef.current[editIndex];
    if (editIndex >= 0 && row) {
      setDraft({
        ...initialPassenger,
        ...row,
        used_contact: row.used_contact || null,
      });
    } else {
      setDraft(initialPassenger);
    }
  }, [editIndex]);

  function handleChange(e) {
    const { name, value } = e.target;
    setDraft((d) => ({ ...d, [name]: value }));
  }

  function validate() {
    return Boolean(
      (draft.used_name || "").trim() &&
        (draft.report_address || "").trim() &&
        (draft.drop_address || "").trim(),
    );
  }

  function onCancel() {
    setEditIndex(-1);
    setDraft(initialPassenger);
  }

  function onAddOrUpdate() {
    if (!validate()) {
      alert("Please enter name, report and drop addresses.");
      return;
    }

    const passengerToSend = { ...draft }; // includes id

    if (editIndex === -1) {
      onAdd(passengerToSend);
    } else {
      onEdit(editIndex, passengerToSend);
    }

    setEditIndex(-1);
    setDraft(initialPassenger);
  }

  // Receives { target: { name: "used_name" | "used_mobile" | ..., value } }
  // from the autocomplete (because fieldPrefix="used").
  function handleContactFieldChange(e) {
    const { name, value } = e.target;
    setDraft((d) => ({
      ...d,
      [name]: value,
      // Typing a different name means it is no longer a saved contact
      ...(name === "used_name" ? { used_contact: null, used_by_id: null } : {}),
    }));
  }

  // Called when a contact is picked, added, or the field is cleared.
  function handleContactValueChange(event, value) {
    if (!value) {
      setDraft((d) => ({
        ...d,
        used_contact: null,
        used_name: "",
        used_mobile: "",
        used_email: "",
        used_department: "",
        used_by_id: null,
        used_by_title: "",
      }));
      return;
    }

    const reportAddress = [
      value.address,
      value.area_location,
      value.city,
      value.postal_code,
    ]
      .filter(Boolean)
      .join(", ");

    setDraft((d) => ({
      ...d,
      used_contact: value,
      used_name: `${value.customercontactfirstname || ""} ${
        value.customercontactlastname || ""
      }`.trim(),
      used_mobile: value.customercontactmobile || "",
      used_email: value.customercontactemail || "",
      used_department: value.customercontactdepartment || "",
      used_by_id: value.contactid || value.customercontactid || null,
      used_by_title: value.customercontactsalutation || "",
      used_title: value.customercontactsalutation || d.used_title,
      report_address: reportAddress || d.report_address,
    }));
  }

  return (
    <Card
      title="Passengers"
      action={
        <Box sx={{ display: "flex", gap: 1 }}>
          {editIndex !== -1 && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={onCancel}
              sx={ghostBtn}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onAddOrUpdate}
            disabled={!validate()}
            sx={primaryBtn}
          >
            {editIndex === -1 ? "Add passenger" : "Update passenger"}
          </Button>
        </Box>
      }
    >
      {/* Input form */}
      <Grid cols={4}>
        <Field
          name="used_title"
          label="Title"
          value={draft.used_title}
          onChange={handleChange}
          options={TITLE_OPTIONS}
        />

        <Slot sx={span(2)}>
          <ContactAutocompleteWithAddNew
            label="Used by"
            fieldPrefix="used" // <- matches draft keys: used_name, used_mobile, ...
            contactType="Usedby"
            form={draft}
            contacts={dropdownData?.usedByContacts || EMPTY_LIST}
            formValue={draft.used_name}
            onFormChange={handleContactFieldChange}
            onValueChange={handleContactValueChange}
            clientId={clientId}
            customerId={customerId}
          />
        </Slot>

        <Field
          name="used_mobile"
          label="Mobile"
          value={draft.used_mobile}
          onChange={handleChange}
        />

        <Field
          name="used_email"
          label="Email"
          value={draft.used_email}
          onChange={handleChange}
          sx={span(2)}
        />
        <Field
          name="used_department"
          label="Department"
          value={draft.used_department}
          onChange={handleChange}
          sx={span(2)}
        />

        <Field
          name="report_address"
          label="Report address"
          value={draft.report_address}
          onChange={handleChange}
          multiline
          rows={2}
          sx={span(2)}
        />
        <Field
          name="drop_address"
          label="Drop address"
          value={draft.drop_address}
          onChange={handleChange}
          multiline
          rows={2}
          sx={span(2)}
        />
      </Grid>

      {/* Table of passengers */}
      <Box
        sx={{
          mt: 2.5,
          overflowX: "auto",
          border: `1px solid ${C.border}`,
          borderRadius: "8px",
        }}
      >
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>Name</TableCell>
              <TableCell sx={thSx}>Mobile</TableCell>
              <TableCell sx={thSx}>Email</TableCell>
              <TableCell sx={thSx}>Department</TableCell>
              <TableCell sx={thSx}>Report address</TableCell>
              <TableCell sx={thSx}>Drop address</TableCell>
              <TableCell sx={{ ...thSx, width: 150 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {passengers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ ...tdSx, color: C.muted, py: 3, borderBottom: "none" }}
                >
                  No passengers added yet.
                </TableCell>
              </TableRow>
            ) : (
              passengers.map((p, i) => (
                <TableRow
                  key={i}
                  sx={{
                    backgroundColor:
                      editIndex === i ? C.primarySoft : "transparent",
                    "&:hover": {
                      backgroundColor:
                        editIndex === i ? C.primarySoft : C.surface,
                    },
                    "&:last-of-type td": { borderBottom: "none" },
                  }}
                >
                  <TableCell sx={{ ...tdSx, fontWeight: 500 }}>
                    {[p.used_title, p.used_name].filter(Boolean).join(" ")}
                  </TableCell>
                  <TableCell sx={tdSx}>{p.used_mobile}</TableCell>
                  <TableCell sx={wrapTd}>{p.used_email}</TableCell>
                  <TableCell sx={tdSx}>{p.used_department}</TableCell>
                  <TableCell sx={wrapTd}>{p.report_address || "-"}</TableCell>
                  <TableCell sx={wrapTd}>{p.drop_address || "-"}</TableCell>
                  <TableCell
                    sx={{ ...tdSx, whiteSpace: "nowrap" }}
                    align="center"
                  >
                    <IconBtn
                      color="primary"
                      onClick={() => setEditIndex(i)}
                      title="Edit passenger"
                    >
                      <EditIcon />
                    </IconBtn>
                    <IconBtn
                      color="error"
                      onClick={() => onDelete(i)}
                      title="Delete passenger"
                    >
                      <DeleteIcon />
                    </IconBtn>
                    <IconBtn
                      component="a"
                      href={`https://wa.me/${(p.used_mobile || "").replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Send WhatsApp message"
                      sx={{ color: "#16a34a" }}
                    >
                      <WhatsAppIcon />
                    </IconBtn>
                    <IconBtn
                      component="a"
                      href={`mailto:${p.used_email}`}
                      title="Send email"
                      sx={{ color: C.muted }}
                    >
                      <EmailIcon />
                    </IconBtn>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
}